const verdictColors = {
    'Pass': 'bg-green-100 text-green-800',
    'Fail': 'bg-red-100 text-red-800',
};

const VerdictBadge = ({ verdict }) => {
    if (!verdict) return null;
    const color = verdictColors[verdict] || 'bg-gray-100 text-gray-700';
    return <span className={`inline-block px-2.5 py-1 text-xs font-semibold rounded-full ${color}`}>{verdict}</span>;
};

const fmt = (v) => {
    if (v === null || v === undefined) return '—';
    return typeof v === 'number' ? v.toLocaleString('en-GH', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : v;
};

const pct = (v) => {
    if (v === null || v === undefined || v === 0) return '—';
    return `${(v * 100).toFixed(1)}%`;
};

const YesNoBadge = ({ value }) => {
    if (!value) return <span className="text-xs text-gray-400">—</span>;
    const color = value === 'Yes' ? 'text-green-700' : 'text-red-600';
    return <span className={`text-sm font-medium ${color}`}>{value}</span>;
};

function PensionAnalysisSection({ pensionAnalysis, onChange, readOnly }) {
    const pa = pensionAnalysis || {};

    const formAChecks = [
        { key: 'signedPledgeFormReceived', label: 'Signed pledge form received' },
        { key: 'customerSignaturePresent', label: 'Customer signature present' },
        { key: 'trusteeSignaturePresent', label: 'Trustee signature present' },
        { key: 'formForwarded', label: 'Form forwarded to trustee' },
        { key: 'employerContributionVested', label: 'Employer contribution vested' },
        { key: 'lienOnEmployerContribution', label: 'Lien on employer contribution' },
        { key: 'confirmedEmploymentDate', label: 'Confirmed employment date' },
    ];

    const handleChange = (field, value) => {
        if (onChange) onChange('pensionAnalysis', field, value);
    };

    return (
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-y border-gray-200 rounded-t-lg">
                <h3 className="text-sm font-semibold text-gray-600 uppercase tracking-wide">Pension Analysis</h3>
                <VerdictBadge verdict={pa.ltpVerdict} />
            </div>

            {/* Pension Details */}
            <div className="px-4 pt-3 pb-1">
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Pension Details</span>
            </div>
            <div className="grid grid-cols-2 gap-3 px-4 py-3">
                <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Pension Trustee</label>
                    <input type="text" value={pa.pensionTrustee || ''} disabled={readOnly}
                        onChange={e => handleChange('pensionTrustee', e.target.value)}
                        className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:ring-1 focus:ring-[#1e3a6e] focus:border-[#1e3a6e] disabled:bg-gray-50" />
                </div>
                <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Pension ID</label>
                    <input type="text" value={pa.pensionId || ''} disabled={readOnly}
                        onChange={e => handleChange('pensionId', e.target.value)}
                        className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:ring-1 focus:ring-[#1e3a6e] focus:border-[#1e3a6e] disabled:bg-gray-50" />
                </div>
                <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Total Tier 3 Balance (GHS)</label>
                    <input type="number" value={pa.totalTier3Balance || ''} disabled={readOnly}
                        onChange={e => handleChange('totalTier3Balance', parseFloat(e.target.value) || 0)}
                        className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:ring-1 focus:ring-[#1e3a6e] focus:border-[#1e3a6e] disabled:bg-gray-50" />
                </div>
                <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Vested Collateral (GHS)</label>
                    <input type="number" value={pa.vestedCollateral || ''} disabled={readOnly}
                        onChange={e => handleChange('vestedCollateral', parseFloat(e.target.value) || 0)}
                        className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:ring-1 focus:ring-[#1e3a6e] focus:border-[#1e3a6e] disabled:bg-gray-50" />
                </div>
                <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Avg Monthly Contribution (GHS)</label>
                    <input type="number" value={pa.avgMonthlyContribution || ''} disabled={readOnly}
                        onChange={e => handleChange('avgMonthlyContribution', parseFloat(e.target.value) || 0)}
                        className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:ring-1 focus:ring-[#1e3a6e] focus:border-[#1e3a6e] disabled:bg-gray-50" />
                </div>
                <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Contribution History (Years)</label>
                    <input type="number" value={pa.contributionHistoryYears || ''} disabled={readOnly}
                        onChange={e => handleChange('contributionHistoryYears', parseFloat(e.target.value) || 0)}
                        className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:ring-1 focus:ring-[#1e3a6e] focus:border-[#1e3a6e] disabled:bg-gray-50" />
                </div>
                <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Contributions Last 12 Months</label>
                    <input type="number" value={pa.contributionsLast12Months || ''} disabled={readOnly}
                        onChange={e => handleChange('contributionsLast12Months', parseInt(e.target.value) || 0)}
                        className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:ring-1 focus:ring-[#1e3a6e] focus:border-[#1e3a6e] disabled:bg-gray-50" />
                </div>
            </div>

            {/* Form A Trustee Checklist */}
            <div className="px-4 pt-3 pb-1 border-t border-gray-200">
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Form A — Trustee Checklist</span>
            </div>
            <div className="divide-y divide-gray-100">
                {formAChecks.map(check => (
                    <div key={check.key} className="flex items-center justify-between px-4 py-2.5">
                        <span className="text-sm text-gray-600">{check.label}</span>
                        {readOnly ? (
                            <YesNoBadge value={pa[check.key]} />
                        ) : (
                            <select value={pa[check.key] || ''} onChange={e => handleChange(check.key, e.target.value)}
                                className="rounded-lg border border-gray-200 px-2 py-1 text-sm focus:ring-1 focus:ring-[#1e3a6e]">
                                <option value="">—</option>
                                <option value="Yes">Yes</option>
                                <option value="No">No</option>
                            </select>
                        )}
                    </div>
                ))}
            </div>

            {/* LTP & Eligibility */}
            <div className="px-4 pt-4 pb-1 border-t border-gray-200">
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">LTP & Eligibility</span>
            </div>
            <div className="divide-y divide-gray-100">
                <div className={`flex items-center justify-between px-4 py-3 ${pa.ltp > 0.8 ? 'bg-red-50' : pa.ltp > 0 ? 'bg-green-50' : ''}`}>
                    <span className="text-sm font-medium text-gray-700">Loan-to-Pension (LTP)</span>
                    <div className="flex items-center gap-2">
                        <span className={`text-sm font-bold ${pa.ltp > 0.8 ? 'text-red-700' : 'text-green-700'}`}>
                            {pct(pa.ltp)}
                        </span>
                        <VerdictBadge verdict={pa.ltpVerdict} />
                    </div>
                </div>
                <div className="flex items-center justify-between px-4 py-2.5">
                    <span className="text-sm text-gray-600">Eligible by pension (80% of vested)</span>
                    <span className="text-sm font-medium text-gray-900">GHS {fmt(pa.eligibleByPension)}</span>
                </div>
                <div className="flex items-center justify-between px-4 py-2.5">
                    <span className="text-sm text-gray-600">Eligible by income (60% DTI flat)</span>
                    <span className="text-sm font-medium text-gray-900">GHS {fmt(pa.eligibleByIncome)}</span>
                </div>
                <div className="flex items-center justify-between px-4 py-3 bg-blue-50/50">
                    <span className="text-sm font-semibold text-gray-700">Final eligible amount</span>
                    <span className="text-sm font-bold text-[#1e3a6e]">GHS {fmt(pa.finalEligibleAmount)}</span>
                </div>
            </div>
        </div>
    );
}

export default PensionAnalysisSection;
